import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { API_ENDPOINTS, getAllUsers } from 'src/services';

interface Filter {
  label: string;
  value: string;
}

interface UserState {
  sliceUsers: Array<any>;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  filters: Filter[];
  role: string | null;
  query: string;
}

const initialState: UserState = {
  sliceUsers: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  filters: [],
  role: null,
  query: '',
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (
    {
      __raw,
      pageNumber,
      limit,
      role,
      query,
    }: {
      __raw: string;
      pageNumber: number;
      limit: number;
      role: string | null;
      query: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getAllUsers(
        {
          page: pageNumber,
          per_page: limit,
          role: role || undefined,
          filter: query || undefined,
        },
        __raw,
      );
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401)
          return rejectWithValue({
            type: 'unauthorized',
            message: 'Session expired. Please sign out and sign back in.',
          });
        if (status === 403)
          return rejectWithValue({
            type: 'forbidden',
            message: 'You do not have permission to view users.',
          });
        if (!error.response)
          return rejectWithValue({
            type: 'network',
            message: 'Network error. Check your connection.',
          });
        return rejectWithValue({ type: 'api', status, message: error.message });
      }
      return rejectWithValue({ type: 'unknown', message: String(error) });
    }
  },
);

export const fetchTotalCount = createAsyncThunk(
  'users/fetchTotalCount',
  async (
    {
      __raw,
      role,
      query,
    }: {
      __raw: string;
      role: string | null;
      query: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const roleFilter = role ? `&role=${role}` : '';
      const queryFilter = query ? `&filter=${encodeURIComponent(query)}` : '';
      const response = await axios.get(
        `${API_ENDPOINTS.USERS}/total?${roleFilter}${queryFilter}`
          .replace(/\?&/g, '?')
          .replace(/\?\?/g, '?'),
        {
          headers: {
            Authorization: `Bearer ${__raw}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data.totalCount || response.data.total || 0;
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401)
          return rejectWithValue({
            type: 'unauthorized',
            message: 'Session expired.',
          });
        if (!error.response)
          return rejectWithValue({
            type: 'network',
            message: 'Network error.',
          });
        return rejectWithValue({ type: 'api', status, message: error.message });
      }
      return rejectWithValue({ type: 'unknown', message: String(error) });
    }
  },
);

export const userSlice = createSlice({
  name: 'sliceUser',
  initialState,
  reducers: {
    setRoleFilter(state, action: PayloadAction<string | null>) {
      state.role = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUsers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        // Response structure: { page, per_page, total, users: [...] }
        state.sliceUsers = action.payload?.users || [];
        state.totalCount = action.payload?.total || 0;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as
          | { type: string; message: string }
          | undefined;
        state.error =
          payload?.message ?? action.error.message ?? 'Failed to fetch users';
        state.sliceUsers = [];
      })
      .addCase(fetchTotalCount.fulfilled, (state, action) => {
        state.totalCount = action.payload;
      })
      .addCase(fetchTotalCount.rejected, (state, action) => {
        const payload = action.payload as { message: string } | undefined;
        state.error =
          payload?.message ??
          action.error.message ??
          'Failed to fetch total count';
      });
  },
});

export const { setRoleFilter } = userSlice.actions;

export default userSlice.reducer;
